import { pointer, Toast } from '@byecode/ui'
import { tokenStorage, useTokenRefreshManager, useUploadTokenRefresh } from '@lighthouse/shared'
import { useItemErrorListener, useItemFinishListener, useUploadyContext } from '@rpldy/shared-ui'
import UploadDropZone from '@rpldy/upload-drop-zone'
import { useRequestPreSend } from '@rpldy/uploady'
import React, { useCallback } from 'react'
import styled from 'styled-components'

import { RoundAvatar } from '@/components/RoundAvatar'

interface UploadButtonProps {
    children?: React.ReactNode
    onUpdateImg: (imgSrc: string) => void
    name?: string
    img?: string
    enableTooltip?: boolean
    disabled?: boolean
    radius?: string
}

const SpaceBackground = styled.div`
    ${pointer}
`

const SpaceItemAbbrText = styled.div`
    width: 34px;
    height: 36px;
    line-height: 33px;
    text-align: center;
    background: var(--color-wakaba-light);
    border: 1px solid var(--color-black);
    border-radius: 10px;
    color: var(--color-black);
    font-size: 16px;

    ${pointer}
`

export const UploadBtn: React.FC<UploadButtonProps> = ({ img, enableTooltip = false, onUpdateImg, name, disabled = false, radius }) => {
    const uploady = useUploadyContext()
    const tokenRefreshManager = useTokenRefreshManager()

    // 动态注入 Authorization token
    useRequestPreSend(({ items, options }) => {
        const token = tokenStorage.getAccessToken()

        return {
            items,
            options: {
                ...options,
                destination: {
                    ...options.destination,
                    headers: {
                        ...options.destination?.headers,
                        ...(token ? { Authorization: `Bearer ${token}` } : {})
                    }
                }
            }
        }
    })

    // 处理 token 刷新后的重试
    useUploadTokenRefresh()

    useItemFinishListener(item => {
        const { uploadResponse } = item

        onUpdateImg(uploadResponse.data.content.url)
    })

    useItemErrorListener(async item => {
        // 🔑 处理 401 错误
        if (item.uploadStatus === 401) {
            if (!tokenRefreshManager) {
                Toast.error('认证失败，请重新登录')
                return
            }

            try {
                // 刷新 token
                await tokenRefreshManager.handleUpload401(item.id)
                // 触发重试
                const { uploadManageEvents } = await import('@lighthouse/shared/src/components/UploadManage/hooks/events')
                uploadManageEvents.emit('retry-item', { itemId: item.id })
            } catch {
                Toast.error('上传失败，请重试')
            }
            return
        }

        // 其他错误
        Toast.error('上传失败，请重试')
    })

    const handleUpload = useCallback(() => {
        if (disabled) {
            return
        }
        uploady.showFileUpload()
    }, [disabled, uploady])

    return (
        <UploadDropZone>
            <SpaceBackground onClick={handleUpload}>
                <RoundAvatar
                    enableTooltip={enableTooltip}
                    tooltipLabel={`空间：${name}`}
                    background="var(--color-gray-50)"
                    activeBgColor="var(--color-white)"
                    activeColor="var(--color-black)"
                    color="var(--color-black)"
                    radius={radius}
                    style={{
                        borderColor: 'var(--color-gray-200)'
                    }}
                    icon={img}
                    name={name}
                />
            </SpaceBackground>
        </UploadDropZone>
    )
}
